"use client";

import { useLang } from "@/contexts/language-context";
import { motion } from "framer-motion";
import { ChefHat, CreditCard, MessageSquare, ShoppingBag, UserRound } from "lucide-react";

type Stop = {
  icon: React.ComponentType<{ className?: string }>;
  label: { en: string; es: string };
  caption: { en: string; es: string };
  t: string;
};

const STOPS: Stop[] = [
  {
    icon: UserRound,
    label: { en: "Walks up", es: "Se acerca" },
    caption: { en: "Customer at the window", es: "Cliente en la ventana" },
    t: "00:00",
  },
  {
    icon: ShoppingBag,
    label: { en: "4 taps", es: "4 taps" },
    caption: { en: "Operator builds order", es: "Operador arma orden" },
    t: "00:08",
  },
  {
    icon: CreditCard,
    label: { en: "Tap to pay", es: "Tap para pagar" },
    caption: { en: "North terminal · approved", es: "Terminal North · aprobado" },
    t: "00:14",
  },
  {
    icon: ChefHat,
    label: { en: "On the line", es: "En la línea" },
    caption: { en: "KDS ticket fires", es: "Ticket KDS dispara" },
    t: "00:15",
  },
  {
    icon: MessageSquare,
    label: { en: "Ready 🌮", es: "Listo 🌮" },
    caption: { en: "SMS sent to customer", es: "SMS al cliente" },
    t: "01:24",
  },
];

export default function FoodTruckQsrCounterFlow() {
  const { t, lang } = useLang();

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-[1100px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <div className="inline-block text-[10px] font-bold uppercase tracking-[0.2em] text-redy-orange mb-4">
            {t("The counter flow", "El flujo del mostrador")}
          </div>
          <h2 className="text-[40px] sm:text-5xl md:text-[60px] font-black leading-[1.04] tracking-tight text-redy-charcoal mb-4">
            {t("Six taps. Ten dollars.", "Seis taps. Diez pesos.")}
            <br />
            <span className="redy-text-gradient">{t("Done.", "Listo.")}</span>
          </h2>
          <p className="text-lg text-redy-secondary max-w-[560px] mx-auto font-medium">
            {t(
              "No tables. No servers. No reservations. Just a window, a tap, and a customer who's already on their way back to the car.",
              "Sin mesas. Sin meseros. Sin reservas. Solo una ventana, un tap, y un cliente que ya va de regreso al carro."
            )}
          </p>
        </motion.div>

        {/* Horizontal timeline */}
        <div className="relative">
          {/* Connecting line */}
          <div className="absolute top-[40px] left-[5%] right-[5%] h-[2px] hidden md:block">
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              style={{ transformOrigin: "left center" }}
              className="h-full bg-gradient-to-r from-redy-orange via-redy-red to-redy-charcoal"
            />
          </div>

          <ol className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4 relative">
            {STOPS.map((stop, i) => {
              const Icon = stop.icon;
              return (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  className="relative flex flex-col items-center text-center"
                >
                  <div className="relative w-20 h-20 bg-white border-2 border-redy-charcoal rounded-full flex items-center justify-center shadow-[0_8px_20px_-8px_rgba(0,0,0,0.15)] z-10">
                    <Icon className="w-8 h-8 text-redy-charcoal" />
                    <div className="absolute -top-2 -right-2 bg-redy-orange text-white text-[10px] font-black rounded-full w-7 h-7 flex items-center justify-center font-mono">
                      {i + 1}
                    </div>
                  </div>
                  <div className="mt-5 font-mono tabular-nums text-xs font-bold text-redy-orange">
                    {stop.t}
                  </div>
                  <div className="mt-1 font-bold text-redy-charcoal text-base">
                    {stop.label[lang as "en" | "es"] ?? stop.label.en}
                  </div>
                  <div className="mt-1 text-xs text-redy-charcoal/60 leading-snug max-w-[140px]">
                    {stop.caption[lang as "en" | "es"] ?? stop.caption.en}
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 bg-redy-charcoal text-white rounded-3xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <div className="text-center md:text-left">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-redy-orange mb-2">
              {t("Built for the window", "Hecho para la ventana")}
            </div>
            <div className="text-2xl md:text-3xl font-black leading-tight">
              {t(
                "Fast counter orders, built for the rush.",
                "Órdenes de mostrador rápidas, hechas para la hora pico."
              )}
            </div>
          </div>
          <div className="flex gap-6 text-center">
            <div>
              <div className="font-mono tabular-nums text-3xl md:text-4xl font-black text-redy-orange">
                4
              </div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 mt-1">
                {t("taps to build (demo)", "taps para armar (demo)")}
              </div>
            </div>
            <div className="w-px bg-white/10" />
            <div>
              <div className="font-mono tabular-nums text-3xl md:text-4xl font-black text-redy-orange">
                1
              </div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 mt-1">
                {t("tap to pay", "tap para pagar")}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
